/**
 * phaseProgression.js — helper centralisé pour les transitions de phase
 * action-based (08/02/2026, refonte phases).
 *
 * Refonte par le besoin user : la progression n'est plus pilotée par des
 * seuils CA/temps/flotte (cf. ancien bloc dans coreSlice.nextDay) mais par
 * des actions concrètes du joueur :
 *
 *   - P1 → P2 : achat de l'investissement « Agrandissement dépôt (x2) » (8 k€).
 *   - P2 → P3 : ouverture d'un dépôt supplémentaire en France OU rachat
 *               d'un concurrent OU achat de l'investissement « Extension
 *               logistique (+100 km) » (radius +100 km, QG ou dépôt).
 *   - P3 → P4 : ouverture d'un dépôt à l'étranger OU achat d'un véhicule
 *               Tier 5.
 *
 * Plus de Phase 5 (les upgrades énergie verte sont remontés en P4).
 *
 * Effets de bord uniformes à chaque promotion :
 *   1. set({ phase: newPhase, pendingPhaseUp: newPhase })
 *      → réutilise la modale existante "PendingPhaseUp" qui affiche un
 *        dialogue contextuel + tutoriel.
 *   2. triggerMilestone(`phase_${newPhase}_unlocked`, '/strategie')
 *      → bump du badge nav (existant).
 *   3. triggerMilestoneVideo({ milestone: 'phase-up', phase: newPhase })
 *      → modale plein écran avec la vidéo Sora 2 dédiée + bouton "OK".
 *   4. pushCustomMessage(...) — article de presse fictif marquant la
 *      bascule, lisible dans la boîte mail.
 */

/**
 * Article de presse pour chaque bascule. Le rédacteur change selon la
 * phase pour donner du grain narratif sans tomber dans la répétition.
 *
 * 08/02/2026 — Q2 user request : version EN ajoutée pour l'expérience
 * complète en anglais. Le sélecteur lit la langue courante via
 * `useLanguageStore.getState().language` (lazy require pour éviter une
 * dépendance circulaire avec React).
 */
const PRESS_ARTICLES_FR = {
  2: {
    senderName: 'Gazette des Transports',
    senderRole: 'Rédaction régionale',
    subject: 'Une PME de transport monte en cadence : tour des dépôts agrandis',
    body:
      `Notre rédaction a remarqué qu’un acteur logistique local vient de doubler la capacité de son dépôt principal. ` +
      `Avec ce premier agrandissement, l’entreprise franchit officiellement le cap du **régional** : elle peut désormais ` +
      `capter des contrats Tier 3 (lots inter-régionaux, frigo longue distance, BTP semi-lourd).\n\n` +
      `Un signal pour le marché : derrière les chiffres encore modestes, c’est une stratégie d’industrialisation ` +
      `qui se met en place. Affaire à suivre.`,
  },
  3: {
    senderName: 'Le Logisticien',
    senderRole: 'Magazine professionnel',
    subject: 'Cap national franchi — un nouvel acteur s’installe dans le top 50',
    body:
      `L’entreprise vient de franchir une étape majeure : extension de son maillage (nouveau dépôt, rachat de ` +
      `concurrent ou couverture +100 km — selon les sources). Ce mouvement la propulse sur la scène **nationale** : ` +
      `accès aux contrats Tier 4 et Tier 5 (ADR, convoi exceptionnel, porte-engins), aux marchés publics, et aux ` +
      `AO inter-régionaux.\n\n` +
      `Les analystes saluent une montée en gamme rapide. La concurrence régionale, elle, commence à s’inquiéter.`,
  },
  4: {
    senderName: 'Europe Logistique Hebdo',
    senderRole: 'Rédaction internationale',
    subject: 'Une PME française franchit le pas — opérations transfrontalières lancées',
    body:
      `L’entreprise vient d’inaugurer son premier dépôt à l’étranger (ou de mettre en service un véhicule Tier 5 ` +
      `pour les rotations EU — selon les premières informations). Elle entre officiellement dans la cour des ` +
      `transporteurs **internationaux** : licence EU, douane, fiscalité multi-pays, retraite de chef d’entreprise ` +
      `calculable, et pourquoi pas demain : flotte verte.\n\n` +
      `Un saut de complexité considérable, mais aussi un changement d’échelle qui ouvre la porte aux marchés ` +
      `européens à 4-8 k€ la rotation.`,
  },
};

const PRESS_ARTICLES_EN = {
  2: {
    senderName: 'Transport Gazette',
    senderRole: 'Regional desk',
    subject: 'Local hauler shifts gears: depot expansions across the region',
    body:
      `Our newsroom has noticed that a local logistics player has just doubled the capacity of its main depot. ` +
      `With this first expansion, the company officially crosses the **regional** threshold: it can now ` +
      `take on Tier 3 contracts (interregional batches, long-haul refrigerated, mid-weight construction).\n\n` +
      `A signal for the market: behind the still-modest numbers, this is a deliberate industrialisation ` +
      `strategy taking shape. One to watch.`,
  },
  3: {
    senderName: 'The Logistician',
    senderRole: 'Industry magazine',
    subject: 'National threshold crossed — a new player breaks into the top 50',
    body:
      `The company has just hit a major milestone: extension of its network (new depot, competitor buyout ` +
      `or +100 km coverage — depending on sources). The move propels them onto the **national** stage: ` +
      `access to Tier 4 and Tier 5 contracts (ADR, heavy haulage, equipment transporters), public ` +
      `tenders and interregional bids.\n\n` +
      `Analysts welcome a rapid move up-market. Regional competitors, on the other hand, are starting to worry.`,
  },
  4: {
    senderName: 'Europe Logistics Weekly',
    senderRole: 'International desk',
    subject: 'A French SME takes the leap — cross-border operations underway',
    body:
      `The company has just inaugurated its first depot abroad (or commissioned a Tier 5 vehicle for EU ` +
      `rotations — according to initial reports). It officially joins the league of **international** ` +
      `hauliers: EU licence, customs, multi-country taxation, CEO pension entitlement, and perhaps ` +
      `tomorrow: a green fleet.\n\n` +
      `A considerable jump in complexity, but also a step-change that opens the door to European ` +
      `markets at €4–8k per rotation.`,
  },
};

function getPressArticles() {
  try {
    // Lazy require to avoid circular dep with React-bound i18n init.
    const { useLanguageStore } = require('../../i18n');
    const lang = useLanguageStore.getState().language;
    return lang === 'en' ? PRESS_ARTICLES_EN : PRESS_ARTICLES_FR;
  } catch (_) {
    return PRESS_ARTICLES_FR;
  }
}

/**
 * Tente de promouvoir l'entreprise vers `targetPhase`. No-op si déjà au
 * niveau ou au-dessus. Renvoie true si la promotion a eu lieu, false sinon.
 *
 * @param {Function} get  — Zustand get()
 * @param {Function} set  — Zustand set()
 * @param {number}   targetPhase
 * @param {string}   reasonLabel — courte chaîne loggée pour debug
 */
export function tryPromotePhase(get, set, targetPhase, reasonLabel = '') {
  const state = get();
  const currentPhase = state.phase || 1;
  if (currentPhase >= targetPhase) return false;

  // 1) Mutation phase + pending UI hint.
  set({
    phase: targetPhase,
    pendingPhaseUp: targetPhase,
  });

  // 2) Nav badge milestone (idempotent).
  if (typeof get().triggerMilestone === 'function') {
    try { get().triggerMilestone(`phase_${targetPhase}_unlocked`, '/strategie'); }
    catch (err) { console.warn('[phaseProgression] triggerMilestone failed:', err); }
  }

  // 3) Sora 2 video — phase_up generic clip (réutilise vehicle-tier-up
  // pour P2, depot-open pour P3, competitor-buyout pour P4 selon le
  // déclencheur OU phase-specific clip si présent).
  if (typeof get().triggerMilestoneVideo === 'function') {
    try {
      get().triggerMilestoneVideo({
        kind: `phase-up-${targetPhase}`,
        phase: targetPhase,
        reason: reasonLabel,
      });
    } catch (err) { console.warn('[phaseProgression] video trigger failed:', err); }
  }

  // 4) Press article (b. only — pas de mail PDG narratif sur cette
  // bascule, l'article suffit pour ancrer le moment).
  const press = getPressArticles()[targetPhase];
  if (press && typeof get().pushCustomMessage === 'function') {
    try {
      get().pushCustomMessage({
        kind: 'news',
        senderName: press.senderName,
        senderRole: press.senderRole,
        subject: press.subject,
        body: press.body,
        ttlDays: 30,
        category: 'press',
      });
    } catch (err) { console.warn('[phaseProgression] press article failed:', err); }
  }

  return true;
}

/**
 * Plafond de Tier de contrats visible/exécutable selon la phase.
 *
 *   P1 (Local)        → Tier 1-2
 *   P2 (Régional)     → +Tier 3
 *   P3 (National)     → +Tier 4-5
 *   P4 (International)→ +contrats internationaux (`international` flag)
 *
 * Utilisé par helpers.generateAvailableContracts pour gater l'apparition
 * sur le board ET par les tris.
 */
export function getMaxContractTierForPhase(phase = 1) {
  if (phase >= 3) return 5;
  if (phase >= 2) return 3;
  return 2;
}

/**
 * True si la phase actuelle autorise les contrats internationaux (UE / EU
 * intra ou hors-UE). Indépendant du Tier — la dimension international
 * passe par le flag `international` du contrat.
 */
export function allowsInternationalContracts(phase = 1) {
  return (phase || 1) >= 4;
}
